
import { ThunkDispatch } from 'redux-thunk';
import { Action, DateRangeField, DateType, AdditionalCriteriaType } from 'app/pages/DiscountEditor/actionTypes';
import { changeSchoolSelection, offeringsLoadStarted, offeringsLoadError, agentSearchTermChanged, agentSearchResultsLoaded, agentSearchResultSelected, agentOfficesLoaded, changeAgentOfficeSelection, saveDiscountAction, changeSchoolGroupSelection, changeCentreGroupSelection, agentSearchFailed, agentOfficeLoadFailed, updateEditableBadedDiscount, feesLoaded, catalogProductsLoaded, setDate, setNeverExpires } from "app/pages/DiscountEditor/actions";
import { getAllFeesForDivision, getProductNames } from "app/shared/api";
import { canEditApplicableProducts, getSelectedSchools, getOfferingSchoolCodes, getProductMap, getSchoolFilterOptions, getProductFilter, productCodeAliases } from "app/pages/DiscountEditor/modules/ProductSelection";
import { getSelectedDivisionIds } from "app/pages/DiscountEditor/modules/Divisions/selectors";
import { setSelectedDivisions, resetApplicableProducts } from "app/pages/DiscountEditor/modules/Divisions/actions";
import { offeringsInvalidateForSchools } from "app/pages/DiscountEditor/actions";
import { searchAgents, getAgentsById } from "app/shared/api/searchAgents";
import { AgentSearchResult } from "app/shared/model/Agent";
import { isAgentSelected, getAgentOffices } from "app/pages/DiscountEditor/modules/Agent";
import { getDiscountActionToEdit, getSavedDiscountActions } from "app/pages/DiscountEditor/modules/DiscountActions";
import { getAllCurrencies } from "app/pages/DiscountEditor/modules/RefData";
import { DiscountValueType, DiscountActionTargetTypeEnum, DiscountBandTypeEnum } from "app/shared/model";
import { changeDiscountActionValueType, startAddingDiscountAction } from "app/pages/DiscountEditor/actions";
import { buildSeededDiscountTarget } from "app/pages/DiscountEditor/modules/DiscountActions/seedDiscountTarget";
import { getProductDateRange } from "app/pages/DiscountEditor/modules/DiscountDates/selectors";
import { loadApplicableProductOfferings } from "app/pages/DiscountEditor/loadApplicableProductOfferings";
import { getSelectedProductCodes, ProductFilter } from "app/shared/model/ProductFilter";
import type { Product } from "app/shared/model";
import type { ProductOffering } from "app/shared/model/ProductOffering";
import { ProductType } from "app/shared/model/Product";
import { getAdditionalProductFilterByType } from "app/pages/DiscountEditor/modules/AdditionalCriteria";
import type { ProductMap } from "app/pages/DiscountEditor/modules/ProductSelection/state";

function productFromAdminCode(code: string): Product | undefined {
    const match = code.match(/^(Course|AccommodationRoom|Accommodation|AddOn|Transfer|Package)-(-?[0-9]+)$/i);
    if (!match) return undefined;
    const rawType = match[1].toLowerCase();
    const type = rawType.startsWith("accommodation")
        ? ProductType.Accommodation
        : rawType === "addon"
            ? ProductType.AddOn
            : rawType === "transfer"
                ? ProductType.Transfer
                : rawType === "package"
                    ? ProductType.Package
                    : ProductType.Course;
    return {
        code,
        name: code,
        type,
        active: true,
    } as Product;
}

function findNamedProduct(products: Product[] | ProductMap, code: string): Product | undefined {
    const aliases = productCodeAliases(code);
    if (Array.isArray(products)) {
        return products.find((product) => aliases.includes(product.code));
    }
    for (const alias of aliases) {
        if (products[alias]) return products[alias];
    }
    return undefined;
}

function collectSelectedProductCodes(state: any): string[] {
    const codes = new Set<string>();
    const addFromFilter = (filter?: ProductFilter) => {
        if (!filter) return;
        Object.keys(getSelectedProductCodes(filter)).forEach((code) => {
            if (code) codes.add(code);
        });
    };

    addFromFilter(getProductFilter(state));

    getSavedDiscountActions(state).forEach((action) => {
        if (action.target.type === DiscountActionTargetTypeEnum.OtherProducts) {
            addFromFilter(action.target.products);
        }
    });

    [
        AdditionalCriteriaType.Course,
        AdditionalCriteriaType.Accommodation,
        AdditionalCriteriaType.AddOns,
        AdditionalCriteriaType.Transfer,
    ].forEach((type) => {
        const section = getAdditionalProductFilterByType(state, type);
        if (section.hasCriteria) {
            addFromFilter(section.productFilter);
        }
    });

    return [...codes];
}

function namedProductForCode(code: string, catalog: Product[], previous: ProductMap): Product | undefined {
    const fromCatalog = findNamedProduct(catalog, code);
    if (fromCatalog) {
        return { ...fromCatalog, code };
    }
    const fromPrevious = findNamedProduct(previous, code);
    if (fromPrevious) {
        return { ...fromPrevious, code };
    }
    return productFromAdminCode(code);
}

function mergeOfferingsWithNamedSelections(loaded: Product[], state: any, catalog: Product[]): Product[] {
    const previous = getProductMap(state) || {};
    const merged = new Map<string, Product>();

    loaded.forEach((product) => {
        const catalogMatch = findNamedProduct(catalog, product.code);
        const previousMatch = findNamedProduct(previous, product.code);
        const name = product.name && product.name !== product.code
            ? product.name
            : (catalogMatch?.name || previousMatch?.name || product.name);
        merged.set(product.code, { ...product, name });
    });

    collectSelectedProductCodes(state).forEach((code) => {
        if (!code || merged.has(code)) return;
        const aliasHit = productCodeAliases(code)
            .map((alias) => merged.get(alias))
            .find((product) => !!product);
        if (aliasHit) {
            merged.set(code, { ...aliasHit, code });
            return;
        }
        const named = namedProductForCode(code, catalog, previous);
        if (named) {
            merged.set(code, named);
        }
    });

    return [...merged.values()];
}

function mergeNamedProducts(products: Product[]): Product[] {
    const byCode = new Map<string, Product>();
    const add = (product: Product) => {
        if (!product?.code) return;
        for (const alias of productCodeAliases(product.code)) {
            byCode.set(alias, { ...product, code: alias });
        }
    };
    (productNameCatalog || []).forEach(add);
    products.forEach(add);
    productNameCatalog = [...byCode.values()];
    return productNameCatalog;
}

let productNameCatalog: Product[] | null = null;

function getProductNameCatalog(codes: string[]): Promise<Product[]> {
    const unresolved = codes.filter((code) => {
        const named = findNamedProduct(productNameCatalog || [], code);
        return !named || !named.name || named.name === named.code;
    });

    if (unresolved.length === 0) {
        return Promise.resolve(productNameCatalog || []);
    }

    return getProductNames(unresolved)
        .then((products) => mergeNamedProducts(products))
        .catch(() => productNameCatalog || []);
}

let offeringsRequestId = 0;

export function refreshApplicableProductOfferingsCommand() {
    return function(dispatch: ThunkDispatch<any, any, Action>, getState: () => any) {
        const requestId = ++offeringsRequestId;
        const state = getState();
        const selectedSchoolMap = getSelectedSchools(state);
        const selectedSchoolCodes = Object.keys(selectedSchoolMap).filter((code) => selectedSchoolMap[code]);
        const selectedCodes = collectSelectedProductCodes(state);

        mergeNamedProducts(
            Object.values(getProductMap(state) || {}).filter((product) =>
                !!product?.name && product.name !== product.code
            )
        );

        const applyNamedProducts = (
            offerings: Product[],
            catalog: Product[],
            schoolOfferings?: ProductOffering[],
            loadedSchoolCodes?: string[]
        ) => {
            if (requestId !== offeringsRequestId) return;
            dispatch(catalogProductsLoaded(
                mergeOfferingsWithNamedSelections(offerings, getState(), catalog),
                schoolOfferings,
                loadedSchoolCodes
            ));
        };

        if (!canEditApplicableProducts(state)) {
            getProductNameCatalog(selectedCodes).then((catalog) => applyNamedProducts([], catalog));
            return;
        }

        const divisionId = getSelectedDivisionIds(state).find((id) => id > 0);
        if (!divisionId) {
            getProductNameCatalog(selectedCodes).then((catalog) => applyNamedProducts([], catalog));
            return;
        }

        const allSchools = getSchoolFilterOptions(state);
        const selectedSchools = allSchools.filter((school) => selectedSchoolMap[school.code]);
        const productDates = getProductDateRange(state);

        dispatch(offeringsLoadStarted(selectedSchoolCodes));
        loadApplicableProductOfferings({
            divisionId,
            selectedSchools,
            productDateFrom: productDates.from,
            productDateTo: productDates.neverExpires ? undefined : productDates.to,
        }).then((result) => {
            applyNamedProducts(result.products, productNameCatalog || [], result.offerings, result.loadedSchoolCodes);
            getProductNameCatalog(selectedCodes).then((catalog) =>
                applyNamedProducts(result.products, catalog, result.offerings, result.loadedSchoolCodes)
            );
        }).catch((err) => {
            if (requestId !== offeringsRequestId) return;
            getProductNameCatalog(selectedCodes).then((catalog) => {
                applyNamedProducts([], catalog);
                dispatch(offeringsLoadError(err?.toString?.() || 'Failed to load product offerings', selectedSchoolCodes));
            });
        });
    }
}

export function changeSchoolSelectionCommand(isSelected: boolean, schoolCode: string) {
    return function(dispatch: ThunkDispatch<any, any, Action>, getState: () => any) {
        dispatch(changeSchoolSelection(isSelected, schoolCode))
        dispatch(refreshApplicableProductOfferingsCommand());
    }
}

export function changeSchoolGroupSelectionCommand(isSelected: boolean, groupCode: string) {
    return function(dispatch: ThunkDispatch<any, any, Action>, getState: () => any) {
        dispatch(changeSchoolGroupSelection(isSelected, groupCode))
        dispatch(refreshApplicableProductOfferingsCommand());
    }
}

export function changeCentreGroupSelectionCommand(isSelected: boolean, centreCode: string) {
    return function(dispatch: ThunkDispatch<any, any, Action>, getState: () => any) {
        dispatch(changeCentreGroupSelection(isSelected, centreCode))
        dispatch(refreshApplicableProductOfferingsCommand());
    }
}

export function changeDivisionSelectionCommand(divisionIds: number[]) {
    return function(dispatch: ThunkDispatch<any, any, Action>, getState: () => any) {
        (dispatch as any)(setSelectedDivisions(divisionIds));
        (dispatch as any)(resetApplicableProducts());
        const state = getState();
        const schoolCodesToInvalidate = getOfferingSchoolCodes(state);
        if (schoolCodesToInvalidate.length > 0) {
            dispatch(offeringsInvalidateForSchools(schoolCodesToInvalidate));
        }
        if (divisionIds.length > 0) {
            getAllFeesForDivision(divisionIds[0]).then(fees => dispatch(feesLoaded(fees)));
        } else {
            dispatch(feesLoaded([]));
        }
        dispatch(refreshApplicableProductOfferingsCommand());
    };
}

export function setDateCommand(type: DateType, field: DateRangeField, date: string) {
    return function(dispatch: ThunkDispatch<any, any, Action>) {
        dispatch(setDate(type, field, date));
        dispatch(refreshApplicableProductOfferingsCommand());
    }
}

export function setNeverExpiresCommand(type: DateType, neverExpires: boolean) {
    return function(dispatch: ThunkDispatch<any, any, Action>) {
        dispatch(setNeverExpires(type, neverExpires));
        dispatch(refreshApplicableProductOfferingsCommand());
    }
}

let searchTimerId : number|undefined = undefined;
export function agentSearchTermChangedCommand(term: string) {
    return function(dispatch: ThunkDispatch<any, any, Action>, getState: () => any) {
        dispatch(agentSearchTermChanged(term));
        term = term.trim();
        if (term === '') {
            dispatch(agentSearchResultsLoaded(term, []))
            return;
        }
        if (searchTimerId !== undefined)
            window.clearTimeout(searchTimerId);
        searchTimerId = window.setTimeout(() => {
            searchTimerId = undefined;
            searchAgents(term, true, 10).then(agents => {
                dispatch(agentSearchResultsLoaded(term, agents))
            })
            .catch(err => {
                dispatch(agentSearchFailed())
            })
        }, 750)
    }
}

export function selectAgentSearchResultSelectedCommand(agent: AgentSearchResult) {
    return function(dispatch: ThunkDispatch<any, any, Action>, getState: () => any) {
        dispatch(agentSearchResultSelected(agent))
        getAgentsById(false, agent.id).then(agents => {
            if (agents.length === 0 || agents[0].id !== agent.id) {
                throw new Error('Cannot find agent: ' + agent.id);
            }
            const offices = agents[0].offices;
            const isSelected = !!isAgentSelected(getState(), agent.id);
            dispatch(agentOfficesLoaded(agent.id, offices, isSelected))
        })
        .catch(err => {
            dispatch(agentOfficeLoadFailed(agent.id))
        });
    }
}

export function changeAgentOfficeSelectionCommand(agentId: string, officeId: string, isSelected: boolean) {
    return function(dispatch: ThunkDispatch<any, any, Action>, getState: () => any) {
        const offices = getAgentOffices(getState(), agentId);
        let isAgentSelected = undefined;
        if (offices != undefined)
        {
            for (const office of offices) {

                const selecion = office.office.id !== officeId ? office.isSelected : isSelected;
                if (isAgentSelected === undefined) {
                    isAgentSelected = selecion;
                    continue;
                }

                if (selecion !== isAgentSelected) {
                    isAgentSelected = undefined;
                    break;
                }
            }
            if (offices.length == 0)
                isAgentSelected = true;
        }

        dispatch(changeAgentOfficeSelection(agentId, officeId, isSelected, isAgentSelected));
    }
}

export function saveDiscountActionCommand() {
    return function (dispatch: ThunkDispatch<any, any, Action>, getState: () => any) {
        const actionToSave = getDiscountActionToEdit(getState());
        if (actionToSave === undefined)
            return;

        const bandsValid = actionToSave.bands.find(x => !x.isValid) === undefined;
        if (!bandsValid) {
            if (!actionToSave.showValidation) {
                dispatch(updateEditableBadedDiscount({
                    ... actionToSave,
                    showValidation: true
                }));
            }
            return;
        }

        const needsCurrency =
            actionToSave.discountValueType === DiscountValueType.AmountPerUnit ||
            actionToSave.discountValueType === DiscountValueType.OverrideUnitPrice;

        if (needsCurrency && !actionToSave.currencyCode) {
            if (!actionToSave.showValidation) {
                dispatch(updateEditableBadedDiscount({ ...actionToSave, showValidation: true }));
            }
            return;
        }

        dispatch(saveDiscountAction(actionToSave));
    }
}

export function changeDiscountActionCurrencyCommand(currencyCode: string) {
    return function (dispatch: ThunkDispatch<any, any, Action>, getState: () => any) {
        if (!currencyCode || typeof currencyCode !== 'string') return;
        const action = getDiscountActionToEdit(getState());
        if (action) {
            dispatch(updateEditableBadedDiscount({ ...action, currencyCode: currencyCode.trim() }));
        }
    };
}

export function changeDiscountActionValueTypeCommand(valueType: DiscountValueType) {
    return function (dispatch: ThunkDispatch<any, any, Action>, getState: () => any) {
        dispatch(changeDiscountActionValueType(valueType));
        if (valueType !== DiscountValueType.AmountPerUnit && valueType !== DiscountValueType.OverrideUnitPrice) return;
        const state = getState();
        const action = getDiscountActionToEdit(state);
        const allCurrencies = getAllCurrencies(state) ?? [];
        if (action && !action.currencyCode && allCurrencies.length > 0) {
            dispatch(updateEditableBadedDiscount({ ...action, currencyCode: allCurrencies[0].code }));
        }
    };
}

export function startAddingDiscountActionCommand(
    target: DiscountActionTargetTypeEnum,
    bandType: DiscountBandTypeEnum
) {
    return function (dispatch: ThunkDispatch<any, any, Action>, getState: () => any) {
        const existingActions = getSavedDiscountActions(getState());
        const initialTarget = buildSeededDiscountTarget(target, existingActions);
        dispatch(startAddingDiscountAction(target, bandType, initialTarget));
    };
}